import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import ToolGrid from '@/components/Tools/ToolGrid';
import { promptsApi } from '@/lib/api';

/** Matches `prompts/[slug].jsx` inner column: maxWidth 800 minus horizontal padding. */
const DETAIL_GRID_MAX_WIDTH = 752;

/**
 * "Related tools" grid on the prompt detail page — Tools the user can run for
 * the same job as this recipe. Data from `GET /json/prompts/:slug/related-tools`.
 */
function PromptRelatedTools({ slug }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const [tools, setTools] = useState(null);

  const loadRelated = useCallback((s, opts) => promptsApi.relatedTools(s, opts), []);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setTools(null);
    (async () => {
      try {
        const data = await loadRelated(slug, { limit: 8 });
        if (cancelled) return;
        setTools(Array.isArray(data?.tools) ? data.tools : []);
      } catch {
        if (!cancelled) setTools([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, loadRelated]);

  const handleToolPress = useCallback((tool) => {
    if (tool?.slug) router.push(`/tools/${tool.slug}`);
  }, [router]);

  const styles = useMemo(() => StyleSheet.create({
    wrap: { gap: 12 },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
    sectionTitle: { marginBottom: 10 },
  }), []);

  if (tools === null) {
    return (
      <View style={styles.wrap}>
        <ActivityIndicator size="small" color={colors.text_color_secondary} />
      </View>
    );
  }
  if (!tools.length) return null;

  return (
    <View style={styles.wrap}>
      <View style={styles.headerRow}>
        <Text tag="h2" size="xxxl" weight="bold" color="primary" style={styles.sectionTitle}>
          {t('pages.tools.relatedTools') || 'Related tools'}
        </Text>
      </View>
      <View style={{ maxWidth: DETAIL_GRID_MAX_WIDTH, width: '100%' }}>
        <ToolGrid
          tools={tools}
          onToolPress={handleToolPress}
        />
      </View>
    </View>
  );
}

export default React.memo(PromptRelatedTools);
